import React from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { 
  Users, Heart, Globe, Navigation, 
  Clock, Shield, Smartphone, Headphones, Search, Filter, 
  Calendar, MessageCircle, Bookmark, Share2, Route, Map 
} from "lucide-react";

const Features = () => {
  const navigate = useNavigate();

  // Structured data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Aaoghumen Travel App",
    "alternateName": ["Aao Ghumen App", "Aao Ghume Travel", "Aaoghume Platform"],
    "applicationCategory": "TravelApplication",
    "operatingSystem": ["Android", "iOS", "Web"],
    "url": window.location.origin,
    "description": "Discover amazing features of Aaoghumen (Aao Ghumen) - AI-powered trip planning, local guides, personalized recommendations, and authentic travel experiences across India.",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "INR"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "15000"
    },
    "featureList": [
      "AI-powered trip planning",
      "Local tour guides",
      "Personalized recommendations", 
      "Multi-language support",
      "Offline maps",
      "Real-time updates",
      "Community reviews",
      "Budget planning"
    ]
  };

  const mainFeatures = [
    {
      icon: <Navigation className="w-12 h-12" />,
      title: "AI-Powered Trip Planning",
      subtitle: "Smart Itinerary Generation",
      description: "Our advanced AI analyzes your preferences, budget, and time constraints to create personalized itineraries that maximize your travel experience.",
      features: ["Custom route optimization", "Budget-friendly suggestions", "Time-efficient planning", "Weather-based recommendations"],
      color: "from-blue-500 to-purple-600"
    },
    {
      icon: <Users className="w-12 h-12" />,
      title: "Local Expert Guides",
      subtitle: "Authentic Local Experiences",
      description: "Connect with verified local guides who share insider knowledge, hidden gems, and authentic cultural experiences you won't find in guidebooks.",
      features: ["Verified local experts", "Multi-language support", "Cultural immersion", "Hidden gem discoveries"],
      color: "from-green-500 to-teal-600"
    },
    {
      icon: <Heart className="w-12 h-12" />,
      title: "Personalized Recommendations",
      subtitle: "Tailored Just for You",
      description: "Machine learning algorithms understand your preferences and suggest destinations, activities, and experiences that match your unique travel style.",
      features: ["Preference learning", "Behavioral analysis", "Custom suggestions", "Adaptive recommendations"],
      color: "from-pink-500 to-red-600"
    },
    {
      icon: <Globe className="w-12 h-12" />,
      title: "Multi-Language Support",
      subtitle: "Travel Without Barriers",
      description: "Explore India confidently with support for Hindi, English, and regional languages, plus real-time translation features.",
      features: ["15+ Indian languages", "Real-time translation", "Voice commands", "Cultural context"],
      color: "from-orange-500 to-yellow-600"
    }
  ];

  const additionalFeatures = [
    { icon: <Map className="w-8 h-8" />, title: "Offline Maps", description: "Navigate without internet connectivity" },
    { icon: <Clock className="w-8 h-8" />, title: "Real-time Updates", description: "Live traffic, weather, and venue information" },
    { icon: <Shield className="w-8 h-8" />, title: "Safety Features", description: "Emergency contacts and location sharing" },
    { icon: <Smartphone className="w-8 h-8" />, title: "Mobile Optimized", description: "Seamless experience across all devices" },
    { icon: <Headphones className="w-8 h-8" />, title: "24/7 Support", description: "Round-the-clock customer assistance" },
    { icon: <Search className="w-8 h-8" />, title: "Advanced Search", description: "Find exactly what you're looking for" },
    { icon: <Filter className="w-8 h-8" />, title: "Smart Filters", description: "Refine results by budget, time, and preferences" },
    { icon: <Calendar className="w-8 h-8" />, title: "Trip Calendar", description: "Organize and schedule your adventures" },
    { icon: <MessageCircle className="w-8 h-8" />, title: "Community Chat", description: "Connect with fellow travelers" },
    { icon: <Bookmark className="w-8 h-8" />, title: "Save Favorites", description: "Bookmark places and experiences" },
    { icon: <Share2 className="w-8 h-8" />, title: "Easy Sharing", description: "Share trips and recommendations" },
    { icon: <Route className="w-8 h-8" />, title: "Route Optimization", description: "Best paths and transportation options" }
  ];

  return (
    <>
      <Helmet>
        <title>Aaoghumen Features - AI Travel Planning & Local Guides | Aao Ghumen App</title>
        <meta name="description" content="Explore powerful features of Aaoghumen (Aao Ghumen) - AI trip planning, local expert guides, personalized recommendations, multi-language support & more. Download Aao Ghume travel app today!" />
        <meta name="keywords" content="aaoghumen features, aao ghumen app, aao ghume travel app, aaoghume platform, ai travel planning, local travel guides, travel app features, india travel app, trip planner, personalized travel recommendations" />
        
        {/* Open Graph Tags */}
        <meta property="og:title" content="Aaoghumen Features - AI Travel Planning & Local Guides" />
        <meta property="og:description" content="Discover amazing features of Aaoghumen - AI-powered trip planning, local guides, personalized recommendations for authentic travel experiences." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${window.location.origin}/features`} />
        <meta property="og:image" content={`${window.location.origin}/logo512.png`} />
        <meta property="og:site_name" content="Aaoghumen" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Aaoghumen Features - AI Travel Planning & Local Guides" />
        <meta name="twitter:description" content="Explore powerful travel features that make trip planning effortless and experiences unforgettable." />
        <meta name="twitter:image" content={`${window.location.origin}/logo512.png`} />
        
        {/* Additional SEO Tags */}
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Aaoghumen Team" />
        <link rel="canonical" href={`${window.location.origin}/features`} />
        
        {/* App Schema */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
        {/* Hero Section */}
        <div className="relative bg-gradient-to-br from-blue-600 via-purple-600 to-indigo-700 text-white overflow-hidden">
          <div className="absolute inset-0 bg-black/10"></div>
          <div className="absolute inset-0 opacity-20">
            <svg className="w-full h-full" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <pattern id="features-grid" width="25" height="25" patternUnits="userSpaceOnUse">
                  <path d="M 25 0 L 0 0 0 25" fill="none" stroke="currentColor" strokeWidth="0.5"/>
                </pattern>
              </defs>
              <rect width="100" height="100" fill="url(#features-grid)" />
            </svg>
          </div>
          
          <div className="relative max-w-7xl mx-auto px-4 py-20 sm:py-28">
            <div className="text-center">
              <div className="mb-6">
                <span className="inline-block bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-semibold">
                  ⚡ Powerful Features
                </span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold mb-6 tracking-tight leading-tight">
                <span className="text-cyan-300">Aaoghumen</span>
                <span className="block text-white">Features</span>
              </h1>
              
              <p className="text-xl sm:text-2xl opacity-90 max-w-4xl mx-auto leading-relaxed mb-8">
                Discover the powerful features that make <strong>Aao Ghumen</strong> your ultimate travel companion - from AI-powered planning to authentic local experiences.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => navigate("/onboarding")}
                  className="bg-white text-purple-600 px-8 py-4 rounded-2xl font-semibold hover:bg-gray-100 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1 text-lg"
                >
                  🚀 Try Features Now
                </button>
                <button
                  onClick={() => navigate("/about")}
                  className="border-2 border-white text-white px-8 py-4 rounded-2xl font-semibold hover:bg-white hover:text-purple-600 transition-all duration-300 text-lg"
                >
                  📖 Learn More
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Features */}
        <div className="max-w-7xl mx-auto px-4 py-20">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-4">
              Core Features of Aao Ghume
            </h2>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto">
              Experience the next generation of travel planning with our innovative features
            </p>
          </div>

          <div className="space-y-20">
            {mainFeatures.map((feature, index) => (
              <div key={index} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`}>
                <div className={`space-y-6 ${index % 2 === 1 ? 'lg:col-start-2' : ''}`}>
                  <div className="flex items-center space-x-4 mb-4">
                    <div className={`bg-gradient-to-r ${feature.color} text-white p-4 rounded-2xl`}>
                      {feature.icon}
                    </div>
                    <div>
                      <h3 className="text-2xl sm:text-3xl font-bold text-gray-800">
                        {feature.title}
                      </h3>
                      <p className="text-purple-600 font-semibold">{feature.subtitle}</p>
                    </div>
                  </div>
                  
                  <p className="text-gray-600 text-lg leading-relaxed">
                    {feature.description}
                  </p>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {feature.features.map((item, idx) => (
                      <div key={idx} className="flex items-center space-x-3">
                        <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                        <span className="text-gray-700">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className={`${index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''}`}>
                  <div className={`bg-gradient-to-br ${feature.color} rounded-3xl p-8 text-white min-h-[400px] flex items-center justify-center relative overflow-hidden`}>
                    <div className="absolute inset-0 opacity-20">
                      <svg className="w-full h-full" viewBox="0 0 100 100">
                        <defs>
                          <radialGradient id={`gradient-${index}`}>
                            <stop offset="0%" stopColor="rgba(255,255,255,0.3)"/>
                            <stop offset="100%" stopColor="rgba(255,255,255,0)"/>
                          </radialGradient>
                        </defs>
                        <circle cx="50" cy="50" r="30" fill={`url(#gradient-${index})`}/>
                      </svg>
                    </div>
                    <div className="text-center relative z-10">
                      <div className="text-6xl mb-4">{feature.icon}</div>
                      <h4 className="text-2xl font-bold mb-2">{feature.title}</h4>
                      <p className="opacity-90">{feature.subtitle}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Additional Features Grid */}
        <div className="bg-gray-50 py-20">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-4">
                More Amazing Features
              </h2>
              <p className="text-gray-600 text-lg max-w-3xl mx-auto">
                Everything you need for seamless travel planning and unforgettable experiences
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {additionalFeatures.map((feature, index) => (
                <div key={index} className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 group hover:-translate-y-2 border border-gray-100">
                  <div className="text-purple-500 mb-4 group-hover:text-purple-600 transition-colors">
                    {feature.icon}
                  </div>
                  <h3 className="text-lg font-bold text-gray-800 mb-2">{feature.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Technology Section */}
        <div className="py-20">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-4">
                Powered by Advanced Technology
              </h2>
              <p className="text-gray-600 text-lg max-w-3xl mx-auto">
                Cutting-edge technology stack that makes Aaoghume the most reliable travel platform
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="bg-gradient-to-br from-blue-500 to-purple-600 text-white w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  🤖
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">Artificial Intelligence</h3>
                <p className="text-gray-600">Machine learning algorithms for personalized recommendations and smart planning</p>
              </div>
              
              <div className="text-center">
                <div className="bg-gradient-to-br from-green-500 to-teal-600 text-white w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  ☁️
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">Cloud Infrastructure</h3>
                <p className="text-gray-600">Scalable, secure, and reliable cloud-based architecture for seamless performance</p>
              </div>
              
              <div className="text-center">
                <div className="bg-gradient-to-br from-orange-500 to-red-600 text-white w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  📱
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">Mobile-First Design</h3>
                <p className="text-gray-600">Optimized for mobile devices with offline capabilities and fast performance</p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-br from-purple-600 via-blue-600 to-indigo-700 py-20">
          <div className="max-w-7xl mx-auto px-4 text-center text-white">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">
              Ready to Experience These Features?
            </h2>
            <p className="text-xl opacity-90 max-w-3xl mx-auto leading-relaxed mb-8">
              Join millions of travelers who rely on <strong>Aaoghumen</strong> features for planning perfect trips, discovering hidden gems, and creating unforgettable memories across India.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate("/onboarding")}
                className="bg-white text-purple-600 px-8 py-4 rounded-2xl font-semibold hover:bg-gray-100 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1 text-lg"
              >
                🎯 Start Using Features
              </button>
              <button
                onClick={() => navigate("/gallery")}
                className="border-2 border-white text-white px-8 py-4 rounded-2xl font-semibold hover:bg-white hover:text-purple-600 transition-all duration-300 text-lg"
              >
                🖼️ View Gallery
              </button>
            </div>
          </div>
        </div>

        {/* SEO Content Footer */}
        <div className="bg-white py-16">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center">
              <h3 className="text-2xl font-bold text-gray-800 mb-4">
                Why Aaoghumen Features Stand Out
              </h3>
              <p className="text-gray-600 text-lg max-w-4xl mx-auto mb-8 leading-relaxed">
                Whether you're searching for "aaoghumen features", "aao ghumen app capabilities", or "aao ghume travel tools" - our platform offers the most comprehensive suite of travel features in India. From AI-powered trip planning to authentic local experiences, <strong>Aaoghume</strong> features are designed to make your journey seamless, personalized, and unforgettable. Experience the future of travel planning with our cutting-edge features today.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
                <div className="text-center">
                  <div className="text-3xl font-bold text-purple-600 mb-2">15M+</div>
                  <div className="text-gray-600">Feature Interactions Daily</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-purple-600 mb-2">50+</div>
                  <div className="text-gray-600">Innovative Features</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-purple-600 mb-2">99.9%</div>
                  <div className="text-gray-600">Feature Reliability</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Features;